import {
  ArrowLeft,
  Crown,
  Edit2,
  LogOut,
  Mail,
  Plus,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import type {
  InviteMemberPayload,
  UpdateGroupPayload,
  User,
} from '../../service/api';
import useGroupStore from '../../store/group';
import useUserStore from '../../store/user';

export default function GroupPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useUserStore(state => state.user);
  const {
    group,
    isLoading,
    error,
    fetchGroup,
    updateGroup,
    leaveGroup,
    inviteMember,
    removeMember,
    changeRole,
  } = useGroupStore();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  const [inviteData, setInviteData] = useState<InviteMemberPayload>({
    email: '',
    role: 'managed',
  });

  const [editData, setEditData] = useState<UpdateGroupPayload>({
    title: '',
  });

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  useEffect(() => {
    if (group) {
      setEditData({ title: group.title });
    }
  }, [group]);

  // Remove the automatic redirect to create page since it's now handled by ManagerProtectedRoutes
  // This allows the page to show loading state properly
  if (isLoading) {
    return (
      <Container className="pt-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="pt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!group) {
    return <Navigate to="/manager/group/create" />;
  }

  const managers = group.managers || [];
  const managed = group.managed || [];
  const isManager = managers.some(m => m._id === currentUser?._id) || false;
  const isLastManager = isManager && managers.length === 1;

  const handleLeaveGroup = async () => {
    if (isLastManager && managed.length > 0) {
      alert(t('groups.lastManager'));
      return;
    }

    if (!window.confirm(t('groups.confirmLeave'))) return;

    try {
      await leaveGroup();
      navigate('/');
    } catch (error) {
      console.error('Failed to leave group:', error);
    }
  };

  const handleInviteMember = async () => {
    setIsProcessing(true);
    setModalError(null);

    try {
      await inviteMember(inviteData);
      setShowInviteModal(false);
      setInviteData({ email: '', role: 'managed' });
      alert(t('groups.inviteModal.success'));
    } catch (error) {
      setModalError(
        error instanceof Error ? error.message : 'Failed to send invitation',
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateGroup = async () => {
    setIsProcessing(true);
    setModalError(null);

    try {
      await updateGroup(group._id, editData);
      setShowEditModal(false);
    } catch (error) {
      setModalError(
        error instanceof Error ? error.message : 'Failed to update group',
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveMember = async (user: User) => {
    if (!window.confirm(t('groups.confirmRemove'))) return;

    setProcessingUserId(user._id);
    try {
      await removeMember({ userId: user._id });
      await fetchGroup();
    } catch (error) {
      console.error('Failed to remove member:', error);
      alert('Failed to remove member. Please try again.');
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleChangeRole = async (
    user: User,
    newRole: 'manager' | 'managed',
  ) => {
    const isChangingLastManager =
      managers.some(m => m._id === user._id) &&
      managers.length === 1 &&
      newRole === 'managed';

    if (isChangingLastManager) {
      alert(t('groups.lastManager'));
      return;
    }

    setProcessingUserId(user._id);
    try {
      await changeRole({ userId: user._id, role: newRole });
      await fetchGroup();
    } catch (error) {
      console.error('Failed to change role:', error);
      alert('Failed to change role. Please try again.');
    } finally {
      setProcessingUserId(null);
    }
  };

  const renderMember = (user: User, role: 'manager' | 'managed') => (
    <Card key={user._id} className="mb-3">
      <Card.Body>
        <Row className="align-items-center">
          <Col xs="auto">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.given_name}
                className="rounded-circle"
                style={{ width: '48px', height: '48px', objectFit: 'cover' }}
              />
            ) : (
              <div
                className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white fw-bold"
                style={{ width: '48px', height: '48px' }}
              >
                {user.given_name[0]}
                {user.family_name[0]}
              </div>
            )}
          </Col>
          <Col>
            <h6 className="mb-1">
              {user.given_name} {user.family_name}
            </h6>
            <p className="text-muted small mb-0">{user.email}</p>
          </Col>
          <Col xs="auto">
            <Badge bg={role === 'manager' ? 'primary' : 'secondary'}>
              {role === 'manager' ? (
                <>
                  <Crown size={14} className="me-1" />
                  {t('groups.manager')}
                </>
              ) : (
                <>
                  <UserCheck size={14} className="me-1" />
                  {t('groups.managedUser')}
                </>
              )}
            </Badge>
          </Col>
          {isManager && user._id !== currentUser?._id && (
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-2 mt-4"
                onClick={() =>
                  handleChangeRole(
                    user,
                    role === 'manager' ? 'managed' : 'manager',
                  )
                }
                disabled={processingUserId === user._id}
              >
                {processingUserId === user._id ? (
                  <Spinner size="sm" />
                ) : (
                  t('groups.changeRole')
                )}
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleRemoveMember(user)}
                disabled={processingUserId === user._id}
                className="mt-4"
              >
                {processingUserId === user._id ? (
                  <Spinner size="sm" />
                ) : (
                  <Trash2 size={16} />
                )}
              </Button>
            </Col>
          )}
        </Row>
      </Card.Body>
    </Card>
  );

  return (
    <div className="min-vh-100 bg-light">
      <Container className="py-4">
        <Row className="mb-4 align-items-center">
          <Col xs="auto">
            <Button
              variant="link"
              className="p-0 text-dark"
              onClick={() => navigate('/')}
            >
              <ArrowLeft size={24} />
            </Button>
          </Col>
          <Col>
            <h2 className="mb-0">{t('groups.groupDetails')}</h2>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h3 className="mb-1">{group.title}</h3>
                <p className="text-muted mb-0">
                  <Users size={16} className="me-1" />
                  {managers.length + managed.length} {t('groups.members')}
                </p>
              </div>
              <div>
                {isManager && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-2"
                    onClick={() => setShowEditModal(true)}
                  >
                    <Edit2 size={16} className="me-1" />
                    {t('common.edit')}
                  </Button>
                )}
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleLeaveGroup}
                >
                  <LogOut size={16} className="me-1" />
                  {t('groups.leaveGroup')}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {isManager && (
          <div className="mb-4">
            <Button variant="primary" onClick={() => setShowInviteModal(true)}>
              <Plus size={16} className="me-1" />
              {t('groups.inviteMember')}
            </Button>
          </div>
        )}

        <h4 className="mb-3">{t('groups.managers')}</h4>
        {managers.map(user => renderMember(user, 'manager'))}

        {managed.length > 0 && (
          <>
            <h4 className="mb-3 mt-4">{t('groups.managed')}</h4>
            {managed.map(user => renderMember(user, 'managed'))}
          </>
        )}
      </Container>

      <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('groups.inviteModal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('groups.inviteModal.email')}</Form.Label>
              <Form.Control
                type="email"
                value={inviteData.email}
                onChange={e =>
                  setInviteData({ ...inviteData, email: e.target.value })
                }
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('groups.inviteModal.role')}</Form.Label>
              <Form.Select
                value={inviteData.role}
                onChange={e =>
                  setInviteData({
                    ...inviteData,
                    role: e.target.value as 'manager' | 'managed',
                  })
                }
              >
                <option value="managed">{t('groups.managedUser')}</option>
                <option value="manager">{t('groups.manager')}</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowInviteModal(false)}
            disabled={isProcessing}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleInviteMember}
            disabled={isProcessing || !inviteData.email}
          >
            {isProcessing ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('groups.inviteModal.sending')}
              </>
            ) : (
              <>
                <Mail size={16} className="me-1" />
                {t('groups.inviteModal.send')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('groups.editModal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          <Form>
            <Form.Group>
              <Form.Label>{t('groups.groupName')}</Form.Label>
              <Form.Control
                type="text"
                value={editData.title || ''}
                onChange={e =>
                  setEditData({ ...editData, title: e.target.value })
                }
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEditModal(false)}
            disabled={isProcessing}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateGroup}
            disabled={isProcessing || !editData.title}
          >
            {isProcessing ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('groups.editModal.saving')}
              </>
            ) : (
              t('groups.editModal.save')
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
